// src/components/weeklyReport/PatientTypeahead.js
//
// Search box whose results appear as the coordinator types (name, DOB or MRN
// digits) over the site's patients. Results render inline under the box, so
// the pop-up around it can't clip them.

import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { searchPatients, sourceLabel, MIN_QUERY_LENGTH } from "../../services/reportPatientSearch";
import { statusLabel as regxStatusLabel } from "../../constants/diseaseOptions";
import { INPUT } from "./ui";

const PatientTypeahead = ({ index, loading, sheetError, onPick, autoFocus = true }) => {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const results = useMemo(() => searchPatients(index, q, { limit: 25 }), [index, q]);

  useEffect(() => {
    setActive(0);
  }, [q]);
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[active]) {
      e.preventDefault();
      onPick(results[active]);
    }
  };

  const typed = q.trim().length >= MIN_QUERY_LENGTH;

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Start typing a name, DOB or MRN…"
          className={`${INPUT} pl-9`}
          aria-label="Search patients"
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
        )}
      </div>
      {sheetError && (
        <p className="weekly-report-warn mt-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">
          The Google Sheet patient list couldn't be loaded, so some patients may be missing from the search.
        </p>
      )}
      <div className="mt-2">
        {!typed ? (
          <p className="text-[11px] text-slate-400 px-1">
            {loading ? "Loading patients…" : "Type at least 2 letters to see matching patients."}
          </p>
        ) : results.length === 0 ? (
          <p className="text-[11px] text-slate-400 px-1">
            {loading ? "Still loading patients…" : "No patients match. Check the spelling, or add them as a patient who isn't in RegX."}
          </p>
        ) : (
          <ul className="max-h-64 overflow-y-auto space-y-1 pr-1" role="listbox" aria-label="Matching patients">
            {results.map((p, i) => (
              <li key={`${p.source}:${p.id}`} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => onPick(p)}
                  className={`weekly-report-result w-full text-left px-3 py-2 rounded-xl border transition ${
                    i === active
                      ? "weekly-report-result--active border-purple-400/40 bg-purple-500/10"
                      : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-white truncate">{p.name || "(no name)"}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{sourceLabel(p.source)}</span>
                  </div>
                  <div className="text-[11px] text-slate-400">
                    MRN {p.mrn || "—"} · DOB {p.dob || "—"}
                    {p.status ? ` · ${regxStatusLabel(p.status)}` : ""}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PatientTypeahead;
